import type { FacturaVenta } from '../../services/facturaVentaService';
import type { Compania } from '../../services/companiaService';

const CODIGO_AFIP_POR_LETRA: Record<string, number> = {
  A: 1,
  B: 6,
  C: 11,
  M: 51,
  E: 19,
};

function soloDigitos(valor: string | null | undefined): string {
  return String(valor ?? '').replace(/\D/g, '');
}

function obtenerDocumentoReceptor(factura: FacturaVenta): { tipoDocRec: number; nroDocRec: number } {
  const docReceptor = soloDigitos(factura.cuit);
  const longitud = docReceptor.length;

  let tipoDocRec = 99;
  if (longitud === 11) tipoDocRec = 80;
  else if (longitud === 8 || longitud === 7) tipoDocRec = 96;

  let nroDocRec = 0;
  if (tipoDocRec !== 99) {
    const parsed = Number.parseInt(docReceptor, 10);
    if (Number.isFinite(parsed)) nroDocRec = parsed;
  }

  const importeTotal = Number(factura.importeTotal) || 0;
  const letra = String(factura.letra ?? '').trim().toUpperCase();
  if (tipoDocRec === 99 && (letra === 'B' || letra === 'C') && importeTotal < 10_000_000) {
    nroDocRec = 0;
  }

  return { tipoDocRec, nroDocRec };
}

function codigoComprobanteAfip(factura: FacturaVenta): number | null {
  const letra = String(factura.letra ?? '').trim().toUpperCase();
  return CODIGO_AFIP_POR_LETRA[letra] ?? null;
}

function fechaEmisionYmd(factura: FacturaVenta): string | null {
  const raw = factura.fechaEmision;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Réplica de AfipQrHelper.GenerarUrlQr (backend) para ticket ESC/POS. */
export function generarAfipQrUrl(factura: FacturaVenta, compania: Compania): string | null {
  const fecha = fechaEmisionYmd(factura);
  if (!fecha) return null;

  const caeDigits = soloDigitos(factura.caeNro);
  if (!caeDigits) return null;

  const codAut = Number.parseInt(caeDigits, 10);
  if (!Number.isFinite(codAut)) return null;

  const cuitEmisor = soloDigitos(compania.cuit);
  if (cuitEmisor.length !== 11) return null;

  const cuitNumerico = Number.parseInt(cuitEmisor, 10);
  if (!Number.isFinite(cuitNumerico)) return null;

  const tipoCmp = codigoComprobanteAfip(factura);
  if (tipoCmp == null) return null;

  const { tipoDocRec, nroDocRec } = obtenerDocumentoReceptor(factura);

  let importe = Number(factura.importeTotal) || 0;
  let moneda = 'PES';
  let cotizacion = 1;

  const codMoneda = Number(factura.codigoMoneda);
  const cotiz = Number((factura as { cotizacion?: number }).cotizacion);
  if (Number.isFinite(codMoneda) && codMoneda !== 0 && Number.isFinite(cotiz) && cotiz > 0) {
    importe = importe / cotiz;
    cotizacion = cotiz;
    moneda = codMoneda === 1 ? 'DOL' : 'PES';
  }

  importe = Math.round(importe * 100) / 100;
  cotizacion = Math.round(cotizacion * 1_000_000) / 1_000_000;

  const payload = {
    ver: 1,
    fecha,
    cuit: cuitNumerico,
    ptoVta: Number(factura.nroSuc) || 0,
    tipoCmp,
    nroCmp: Number(factura.nroComprobante) || 0,
    importe,
    moneda,
    ctz: cotizacion,
    tipoDocRec,
    nroDocRec,
    tipoCodAut: 'E',
    codAut,
  };

  const json = JSON.stringify(payload);
  const base64 = btoa(unescape(encodeURIComponent(json)));

  return `https://www.afip.gob.ar/fe/qr/?p=${base64}`;
}
